import { useEffect, useState } from "react";

interface SoapTilesProps {
  scrollProgress: number;
}

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

const BENEFITS = [
  "Private Discord",
  "Alpha Drops",
  "Web3 Tooling",
];

export default function SoapTiles({ scrollProgress }: SoapTilesProps) {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const easeProgress = clamp((scrollProgress - 0.75) / 0.22, 0, 1);
  // Now slides up from the bottom instead of in from the left
  const mobileMultiplier = isMobile ? 0.5 : 1;
  const translateY = (1 - easeProgress) * 220 * mobileMultiplier;
  const opacity = easeProgress;
  const blurAmount = (1 - easeProgress) * 12;

  // Star-trail layer — diagonal streaks suggesting motion
  const starTrails = Array.from({ length: 48 }).map((_, i) => {
    const top = (i * 53) % 100;
    const left = (i * 37 + 7) % 100;
    const length = 18 + ((i * 13) % 80);
    const opacityDot = 0.25 + ((i * 17) % 70) / 100;
    const rotate = -22 + ((i * 9) % 12);
    return { top, left, length, opacityDot, rotate, key: i };
  });

  return (
    <div
      className="absolute left-1/2 top-1/2 z-40 pointer-events-auto"
      style={{
        transform: `translate(-50%, calc(-50% + ${translateY}px))`,
        opacity,
        filter: `blur(${blurAmount}px)`,
        transition:
          "transform 500ms cubic-bezier(0.16, 1, 0.3, 1), opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), filter 500ms cubic-bezier(0.16, 1, 0.3, 1)",
        willChange: "transform, opacity, filter",
      }}
    >
      <div className="relative w-[420px] sm:w-[680px] md:w-[1040px] grid grid-cols-2 gap-4 md:gap-6 bg-transparent">
        {/* ===== LEFT COLUMN — off-white panel ===== */}
        <div
          className="bg-[#F2EFE6] text-[#0B1B3A] flex flex-col justify-between p-6 sm:p-10 md:p-14"
          style={{ minHeight: "420px" }}
        >
          {/* Top tag row */}
          <div className="flex items-center gap-2 mb-4 md:mb-8">
            <span className="w-1.5 h-1.5 bg-[#0B1B3A]" />
            <span className="font-manrope text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#0B1B3A]/70">
              Vol. 01
            </span>
          </div>

          {/* Headline */}
          <div className="flex-1 flex flex-col justify-center">
            <h3
              className="font-michroma font-normal uppercase text-[#0B1B3A] leading-[0.95]"
              style={{
                fontSize: "clamp(28px, 4.4vw, 64px)",
                letterSpacing: "-0.04em",
              }}
            >
              INNER
              <br />
              CIRCLE
            </h3>

            {/* Dotted divider */}
            <div
              className="my-6 md:my-8 h-px w-full"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to right, #0B1B3A 0, #0B1B3A 2px, transparent 2px, transparent 7px)",
              }}
            />

            {/* Benefit list — concise */}
            <ul className="flex flex-col gap-2 md:gap-3">
              {BENEFITS.map((b, i) => (
                <li
                  key={b}
                  className="flex items-baseline gap-3 font-manrope text-[#0B1B3A] text-[12px] sm:text-[14px] md:text-[16px] leading-snug"
                >
                  <span className="font-michroma text-[9px] sm:text-[10px] md:text-[11px] text-[#0B1B3A]/60 tracking-widest">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="tracking-tight">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Footer hint */}
          <div className="mt-6 md:mt-10 flex items-center justify-between">
            <span className="font-manrope text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#0B1B3A]/60">
              Apply ↗
            </span>
            <span className="font-manrope text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#0B1B3A]/60">
              IC / 2026
            </span>
          </div>
        </div>

        {/* ===== RIGHT COLUMN — split vertically with gap ===== */}
        <div className="flex flex-col gap-4 md:gap-6" style={{ minHeight: "420px" }}>
          {/* Upper: cosmic star trails artwork */}
          <div
            className="relative overflow-hidden flex-1"
            style={{
              background:
                "radial-gradient(120% 80% at 70% 30%, #1E4FA8 0%, #0F2A66 45%, #07153A 80%)",
              minHeight: "230px",
            }}
          >
            {starTrails.map((t) => (
              <span
                key={t.key}
                className="absolute"
                style={{
                  top: `${t.top}%`,
                  left: `${t.left}%`,
                  width: `${t.length}px`,
                  height: "1px",
                  background:
                    "linear-gradient(to right, transparent, rgba(180,210,255,0.95), transparent)",
                  opacity: t.opacityDot,
                  transform: `rotate(${t.rotate}deg)`,
                }}
              />
            ))}
            {[
              { top: 18, left: 22, size: 2 },
              { top: 42, left: 78, size: 3 },
              { top: 65, left: 35, size: 2 },
              { top: 80, left: 60, size: 2 },
              { top: 30, left: 50, size: 4 },
              { top: 55, left: 15, size: 2 },
              { top: 12, left: 70, size: 2 },
            ].map((s, i) => (
              <span
                key={`star-${i}`}
                className="absolute bg-white"
                style={{
                  top: `${s.top}%`,
                  left: `${s.left}%`,
                  width: `${s.size}px`,
                  height: `${s.size}px`,
                  boxShadow: "0 0 8px 1px rgba(180,210,255,0.8)",
                }}
              />
            ))}

            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <span className="w-1 h-1 bg-white" />
              <span className="font-manrope text-white/80 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                Signals · Live
              </span>
            </div>
            <div className="absolute bottom-4 left-4 z-10">
              <span className="font-michroma text-white text-[12px] sm:text-[14px] md:text-[16px] tracking-[0.05em] uppercase">
                Cosmos / 02
              </span>
            </div>
          </div>

          {/* Lower: dark navy block — compact */}
          <div
            className="bg-[#07112D] text-white p-6 sm:p-8 md:p-10 grid grid-cols-2 gap-6 md:gap-10"
            style={{ minHeight: "150px" }}
          >
            <div className="flex flex-col gap-1">
              <span className="font-manrope text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-white/50">
                Members
              </span>
              <span className="font-michroma text-[14px] sm:text-[16px] md:text-[20px] text-white tracking-tight">
                1,284
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-manrope text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-white/50">
                Access
              </span>
              <span className="font-michroma text-[14px] sm:text-[16px] md:text-[20px] text-white tracking-tight">
                Invite Only
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}